import dayjs from "dayjs";

import { BCP47LangCode } from "../i18n.types";

/**
 * Maps each supported app locale to its dayjs locale name and a loader for the
 * corresponding dayjs locale data (dynamic import registers it as a side
 * effect). Keeping the loaders explicit lets the bundler code-split them.
 */
const DAYJS_LOCALE: Record<BCP47LangCode, { name: string; load: () => Promise<unknown> }> = {
  "en-US": { name: "en", load: () => Promise.resolve() },
  "en-CA": { name: "en-ca", load: () => import("dayjs/locale/en-ca") },
  "en-GB": { name: "en-gb", load: () => import("dayjs/locale/en-gb") },
  "es-MX": { name: "es-mx", load: () => import("dayjs/locale/es-mx") },
  "fr-CA": { name: "fr-ca", load: () => import("dayjs/locale/fr-ca") },
  "fr-FR": { name: "fr", load: () => import("dayjs/locale/fr") },
  "de-DE": { name: "de", load: () => import("dayjs/locale/de") },
  "it-IT": { name: "it", load: () => import("dayjs/locale/it") },
  "ja-JP": { name: "ja", load: () => import("dayjs/locale/ja") },
  "ko-KR": { name: "ko", load: () => import("dayjs/locale/ko") },
  "vi-VN": { name: "vi", load: () => import("dayjs/locale/vi") },
  "tl-PH": { name: "tl-ph", load: () => import("dayjs/locale/tl-ph") },
  "zh-Hans": { name: "zh-cn", load: () => import("dayjs/locale/zh-cn") },
  "zh-Hant": { name: "zh-tw", load: () => import("dayjs/locale/zh-tw") },
};

/**
 * Loads and activates the dayjs locale matching the given app locale, so
 * relative-time and localized date output follow the active language. Safe to
 * call repeatedly; falls back to English if the locale data cannot be loaded.
 */
export async function syncDayjsLocale(locale: BCP47LangCode): Promise<void> {
  const entry = DAYJS_LOCALE[locale];
  if (!entry) {
    dayjs.locale("en");
    return;
  }

  try {
    await entry.load();
    dayjs.locale(entry.name);
  } catch (error) {
    console.warn(`[i18n] Unable to load dayjs locale for ${locale}`, error);
    dayjs.locale("en");
  }
}
